import { useQuery } from "@apollo/client/react";
import { OperationTypeWithFee } from "deso-protocol";
import Image from "next/image";
import { useEffect, useState } from "react";
import { LuExternalLink } from "react-icons/lu";

import { waitForTxn } from "@/backend/api-helpers";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoFeeTooltip from "@/components/shared/creator-coin-exchange-modal/deso-fee-tooltip";
import UserRealTimeBalance from "@/components/shared/creator-coin-exchange-modal/user-real-time-balance";
import CurrencyInput from "@/components/shared/dollar-input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SelectTriggerValue } from "@/components/ui/select-trigger-value";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import {
  useUserAccountSignalState,
  useUserBalancesSignalState,
} from "@/contexts/user-context";
import {
  type Account,
  AccountByPublicKeyCachedDocument,
} from "@/graphql/codegen/graphql";
import { useConfetti } from "@/hooks/use-confetti";
import { useTradeCreatorCoin } from "@/hooks/use-trade-creator-coin";
import {
  DESO_APP_NAME,
  DESO_TICKER_DISPLAY_NAME,
  DUSD_TICKER_DISPLAY_NAME,
  TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { desoToUSD, nanosToInteger } from "@/utils/currency";
import { getUserName } from "@/utils/usernames";

interface PreBidFormProps {
  publicKey: string;
}

export const PreBidForm = ({ publicKey }: PreBidFormProps) => {
  const { data: accountData } = useQuery(AccountByPublicKeyCachedDocument, {
    variables: { publicKey },
  });
  const { appState } = useFocusMarketData();

  const account = accountData?.accountByPublicKey as Account;

  const {
    form,
    formValid,
    transactionPreview,
    onTransactionSubmitted,
    ccAvailableCurrencies,
    prepareTransaction,
    ccBalanceNanos,
    loadingCCBalance,
    priceInputSuffix,
    errorMessage,
  } = useTradeCreatorCoin({
    account,
    operationType: OperationTypeWithFee.BID,
    showErrorToast: false,
    orderType: "limit",
  });

  const [submitting, setSubmitting] = useState(false);

  const { user } = useUserAccountSignalState();
  const { realTimeFocusUnlockedBalanceNanos } = useUserBalancesSignalState();
  const { addConfetti } = useConfetti();
  const { toast } = useToast();

  const onSubmit = async () => {
    if (!user || !transactionPreview || !account?.publicKey) {
      return;
    }

    setSubmitting(true);

    try {
      const baseCurrencyPublicKey = account.publicKey;
      const quoteCurrencyPublicKey =
        process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;

      const response = await prepareTransaction({
        spendingLimit: {
          GlobalDESOLimit: transactionPreview.FeeNanos,
          DAOCoinOperationLimitMap: {
            [process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY || ""]: {
              transfer: "UNLIMITED",
            },
          },
          DAOCoinLimitOrderLimitMap: {
            [baseCurrencyPublicKey]: {
              [quoteCurrencyPublicKey]: "UNLIMITED",
            },
            [quoteCurrencyPublicKey]: {
              [baseCurrencyPublicKey]: "UNLIMITED",
            },
          },
        },
      });

      if (!response.submittedTransactionResponse?.TxnHashHex) {
        throw new Error(
          "Transaction was submitted but no transaction hash was returned.",
        );
      }

      await waitForTxn({
        transactionHash: response.submittedTransactionResponse.TxnHashHex,
      });

      await onTransactionSubmitted();
      addConfetti([{ confettiNumber: 1000 }]);
      toast({
        variant: "success",
        title: "Bid Placed",
        description: `Your bid for ${getUserName({
          account,
        })} has been successfully placed!`,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when submitting your limit order`,
        details: createToastErrorDetails(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isReady = formValid && transactionPreview && appState;

  useEffect(() => {
    if (account?.username) {
      form.setValue("depositCurrency", account.username);
    }
  }, [account]);

  return (
    <section>
      <div className="rounded-lg border">
        <div
          className="
            flex flex-col
            sm:flex-row
          "
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="@container w-full"
            >
              <div
                className="
                  flex w-full border-b border-border
                  @max-[500px]:flex-col
                "
              >
                <div
                  className="
                    flex w-1/2 flex-col gap-4 border-b p-4
                    sm:border-r sm:border-b-0
                    @max-[500px]:w-full @max-[500px]:border-r-0
                    @max-[500px]:border-b
                  "
                >
                  <Label
                    className="
                    text-sm whitespace-nowrap text-muted-foreground
                  "
                  >
                    Bid Price
                    <p className="text-xs text-muted">
                      What's your limit price per 1 ${account?.username} token?
                    </p>
                  </Label>
                  <div className="flex flex-row gap-2">
                    <div className="flex w-full gap-x-4">
                      <FormField
                        control={form.control}
                        name="priceCurrency"
                        render={({ field }) => (
                          <FormItem
                            className="
                            flex flex-wrap items-center gap-4
                          "
                          >
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl
                                className="
                                rounded-full border border-border
                              "
                              >
                                <SelectTrigger className="pl-2">
                                  <SelectValue>
                                    {(value) => (
                                      <SelectTriggerValue
                                        value={value}
                                        placeholder="Select a deposit currency"
                                        options={ccAvailableCurrencies
                                          .filter(
                                            (currency) =>
                                              currency.ticker !==
                                              account?.username,
                                          )
                                          .map(
                                            ({
                                              ticker,
                                              icon,
                                              displayName,
                                            }) => ({
                                              value: ticker,
                                              label: displayName,
                                              iconAlt: displayName,
                                              iconSrc: icon,
                                            }),
                                          )}
                                      />
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                              </FormControl>

                              <SelectContent>
                                {ccAvailableCurrencies
                                  .filter((e) => e.ticker !== account?.username)
                                  .map(({ ticker, icon, displayName }) => (
                                    <SelectItem
                                      value={ticker}
                                      key={ticker}
                                      className="cursor-pointer"
                                    >
                                      <div className="flex items-center gap-2">
                                        <div className="size-[22px]">
                                          <Image
                                            src={icon}
                                            alt={displayName}
                                            width={22}
                                            height={22}
                                            className="
                                              min-h-full min-w-full rounded-full
                                              object-cover
                                            "
                                          />
                                        </div>

                                        <span>{displayName}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="price"
                        render={() => (
                          <FormItem className="relative w-full space-y-0">
                            <FormControl>
                              <div>
                                <CurrencyInput
                                  name="price"
                                  className="w-full"
                                  suffix={priceInputSuffix}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className="
                    flex w-1/2 flex-col gap-4 p-4
                    @max-[500px]:w-full @max-[500px]:border-b
                  "
                >
                  <Label
                    className="
                    text-sm whitespace-nowrap text-muted-foreground
                  "
                  >
                    Purchase Amount
                    <p className="text-xs text-muted">
                      How many tokens do you want to buy?
                    </p>
                  </Label>
                  <FormField
                    control={form.control}
                    name="amount"
                    render={() => (
                      <FormItem className="relative w-full space-y-0">
                        <FormControl>
                          <div>
                            <CurrencyInput
                              name="amount"
                              className="w-full"
                              suffix={""}
                              suffixPadding={12}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {(realTimeFocusUnlockedBalanceNanos <= 0 ||
                    errorMessage ||
                    form.formState.errors.amount?.message) && (
                    <div className="text-xs text-red-500">
                      {realTimeFocusUnlockedBalanceNanos <= 0 ? (
                        <>
                          You need {TOKEN_DISPLAY_NAME} to trade. You can top-up
                          or swap with any currency using the buttons above, and
                          you can always cashout to USDC-ERC20 on your Wallet
                          page.
                        </>
                      ) : (
                        errorMessage || form.formState.errors.amount?.message
                      )}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>
        <div
          className="
            flex flex-row items-center justify-between gap-2 px-4 py-2 text-xs
            text-muted
          "
        >
          <span>Available Balance:</span>
          <UserRealTimeBalance
            account={account}
            showCCBalance={false}
            ccBalanceNanos={Number(ccBalanceNanos)}
            loadingCCBalance={loadingCCBalance}
          />
        </div>
      </div>

      <section className="mt-4 rounded-2xl border border-border text-sm">
        <div
          className="
            unforce-radius flex items-center justify-between gap-1 border-b px-3
            py-2
            last:border-b-0
          "
        >
          <span className="font-sans text-xs font-semibold text-muted">
            Your Total Bid
          </span>
          <div className="flex items-center">
            {isReady ? (
              <div
                className="
                  flex flex-col items-end font-mono font-normal
                  text-muted-foreground
                "
              >
                <div className="text-xs">
                  ~
                  <CoinPriceFormatter
                    price={Number(transactionPreview?.LimitAmountInUsd)}
                  />{" "}
                  {DUSD_TICKER_DISPLAY_NAME}
                </div>
                <div className="text-xs text-muted">
                  <CoinPriceFormatter
                    price={Number(transactionPreview?.LimitAmount)}
                    isUsd={false}
                  />{" "}
                  {TOKEN_DISPLAY_NAME}
                </div>
              </div>
            ) : (
              <span className="text-xs text-muted">—</span>
            )}
          </div>
        </div>

        <div
          className="
            unforce-radius flex items-center justify-between gap-1 border-b px-3
            py-2
            last:border-b-0
          "
        >
          <span className="font-sans text-xs font-semibold text-muted">
            Receive Amount
          </span>

          <div className="flex items-center">
            {isReady ? (
              <span>
                {formValid && transactionPreview ? (
                  <div
                    className="
                      flex flex-col items-end font-mono font-normal
                      text-muted-foreground
                    "
                  >
                    <CoinPriceFormatter
                      price={Number(transactionPreview.LimitReceiveAmount)}
                      isUsd={false}
                    />
                    <span className="text-xs text-muted">
                      {getUserName({
                        account,
                      })}{" "}
                      coin
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted">—</span>
                )}
              </span>
            ) : (
              <span className="text-xs text-muted">—</span>
            )}
          </div>
        </div>

        <div
          className="
            unforce-radius flex items-center justify-between gap-1 border-b px-3
            py-2
            last:border-b-0
          "
        >
          <div
            className="
              flex items-center gap-x-1 font-sans text-xs font-semibold
              text-muted
            "
          >
            <span>{DESO_APP_NAME} Network Fee</span>
            <DesoFeeTooltip iconSize={16} />
          </div>

          <div className="flex items-center">
            {isReady ? (
              <div
                className="
                  flex flex-col items-end font-mono font-normal
                  text-muted-foreground
                "
              >
                <div className="text-xs">
                  ~
                  <CoinPriceFormatter
                    maxZeroDigits={6}
                    price={desoToUSD(
                      nanosToInteger(transactionPreview.FeeNanos),
                      appState.USDCentsPerDeSoExchangeRate,
                    )}
                  />{" "}
                  {DUSD_TICKER_DISPLAY_NAME}
                </div>
                <div className="text-xs text-muted">
                  <CoinPriceFormatter
                    maxZeroDigits={6}
                    price={nanosToInteger(transactionPreview.FeeNanos)}
                    isUsd={false}
                  />{" "}
                  {DESO_TICKER_DISPLAY_NAME}
                </div>
              </div>
            ) : (
              <span className="text-xs text-muted">—</span>
            )}
          </div>
        </div>
      </section>

      <div className="flex justify-center gap-4 p-4">
        <Button variant="outline" size="sm">
          Advanced Trade
          <LuExternalLink className="ml-2 size-4 text-muted" />
        </Button>

        <Button
          variant="success"
          size="sm"
          disabled={!isReady || submitting}
          onClick={onSubmit}
        >
          {submitting ? "Placing..." : "Place Your Bid"}
        </Button>
      </div>
    </section>
  );
};
